/**
 * @fileoverview WorkScheduleDialog — hours a person is expected to record on
 * each weekday (MOR-1458).
 *
 * Jess: 7.5 Monday to Thursday and 5.5 on Friday. Paul: nothing on Fridays.
 * Opens on the person's current schedule, or the firm's figure Monday to
 * Friday when they have none, so an admin edits from a sensible week rather
 * than a blank one. "Use firm default" removes their schedule again.
 */

import { useState } from "react";
import { useMutation } from "convex/react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import {
  defaultWeekdayHours,
  MAX_HOURS_PER_DAY,
  WEEKDAY_KEYS,
  WEEKDAY_LABELS,
  weeklyHours,
  type WeekdayKey,
  type WorkSchedule,
} from "@cvx/timesheets/workSchedule";

import { extractErrorMessage } from "@/utils/errors";
import { Button, Label } from "@/ui/primitives";
import { Input } from "@/ui/primitives/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import { DatePicker } from "@/ui/composed/inputs/date-picker";

type HoursText = Record<WeekdayKey, string>;

function toText(hours: WorkSchedule["hours"]): HoursText {
  return Object.fromEntries(
    WEEKDAY_KEYS.map((key) => [key, String(hours[key])]),
  ) as HoursText;
}

function parseHours(text: HoursText): WorkSchedule["hours"] | null {
  const out = { ...defaultWeekdayHours(0) };
  for (const key of WEEKDAY_KEYS) {
    const raw = text[key].trim();
    const hours = raw === "" ? 0 : Number(raw);
    if (!Number.isFinite(hours) || hours < 0 || hours > MAX_HOURS_PER_DAY) {
      return null;
    }
    out[key] = hours;
  }
  return out;
}

export function WorkScheduleDialog({
  open,
  orgId,
  member,
  firmDailyHours,
  onOpenChange,
}: {
  open: boolean;
  orgId: Id<"organizations">;
  member: {
    userId: Id<"users">;
    name: string;
    workSchedule?: WorkSchedule;
  } | null;
  /** The firm's expected daily hours: the starting point for a new schedule. */
  firmDailyHours: number;
  onOpenChange: (open: boolean) => void;
}) {
  const setWorkSchedule = useMutation(api.timesheets.profiles.setWorkSchedule);
  const [saving, setSaving] = useState(false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        {member && (
          <WorkScheduleForm
            key={member.userId}
            member={member}
            firmDailyHours={firmDailyHours}
            saving={saving}
            onSave={async (schedule) => {
              setSaving(true);
              try {
                await setWorkSchedule({
                  orgId,
                  userId: member.userId,
                  schedule,
                });
                toast.success(
                  schedule
                    ? `Work schedule saved for ${member.name}.`
                    : `${member.name} is back on the firm default.`,
                );
                onOpenChange(false);
              } catch (err) {
                toast.error(
                  extractErrorMessage(err, "Could not save the schedule."),
                );
              } finally {
                setSaving(false);
              }
            }}
            onCancel={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * The form is keyed on the person so every open starts from THEIR schedule,
 * with no on-open effect (the dialog focuses its first field as it opens,
 * and a value arriving after that focus would be wiped on blur).
 */
function WorkScheduleForm({
  member,
  firmDailyHours,
  saving,
  onSave,
  onCancel,
}: {
  member: { name: string; workSchedule?: WorkSchedule };
  firmDailyHours: number;
  saving: boolean;
  onSave: (schedule: WorkSchedule | null) => Promise<void>;
  onCancel: () => void;
}) {
  const [hours, setHours] = useState<HoursText>(() =>
    toText(member.workSchedule?.hours ?? defaultWeekdayHours(firmDailyHours)),
  );
  const [startDate, setStartDate] = useState(
    member.workSchedule?.startDate ?? "",
  );
  const [endDate, setEndDate] = useState(member.workSchedule?.endDate ?? "");

  const parsed = parseHours(hours);
  const windowValid = !startDate || !endDate || endDate >= startDate;
  const canSave = parsed !== null && windowValid && !saving;

  return (
    <>
      <DialogHeader>
        <DialogTitle>Work schedule for {member.name}</DialogTitle>
        <DialogDescription>
          Hours expected on each weekday. 0 means a day off. Public holidays in
          the firm's state expect nothing whatever is entered here.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-x-6 gap-y-2">
          {WEEKDAY_KEYS.map((key) => (
            <div key={key} className="flex items-center justify-between gap-3">
              <Label htmlFor={`schedule-${key}`} className="text-sm">
                {WEEKDAY_LABELS[key]}
              </Label>
              <Input
                id={`schedule-${key}`}
                type="number"
                min={0}
                max={MAX_HOURS_PER_DAY}
                step={0.5}
                value={hours[key]}
                onChange={(e) =>
                  setHours((prev) => ({ ...prev, [key]: e.target.value }))
                }
                className="h-8 w-20 tabular-nums"
              />
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">
          {parsed
            ? `${weeklyHours(parsed).toLocaleString("en-AU", { maximumFractionDigits: 2 })} hours a week.`
            : `Each day must be between 0 and ${MAX_HOURS_PER_DAY} hours.`}
        </p>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Starts</Label>
            <DatePicker
              value={startDate}
              onChange={(isoDate) => setStartDate(isoDate ?? "")}
              placeholder="From the start"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Ends</Label>
            <DatePicker
              value={endDate}
              onChange={(isoDate) => setEndDate(isoDate ?? "")}
              placeholder="Ongoing"
            />
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          {windowValid
            ? "Days outside this window expect no time. Leave both blank for always. Nothing is ever expected before the day they joined the firm."
            : "The end date must not be before the start date."}
        </p>
      </div>

      <DialogFooter className="sm:justify-between">
        {member.workSchedule ? (
          <Button
            variant="ghost"
            className="text-muted-foreground"
            disabled={saving}
            onClick={() => void onSave(null)}
          >
            Use firm default
          </Button>
        ) : (
          <span />
        )}
        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            disabled={!canSave}
            onClick={() => {
              if (!parsed) return;
              void onSave({
                hours: parsed,
                ...(startDate ? { startDate } : {}),
                ...(endDate ? { endDate } : {}),
              });
            }}
          >
            {saving && <Loader2 className="size-4 animate-spin" />}
            Save schedule
          </Button>
        </div>
      </DialogFooter>
    </>
  );
}
